import { useGetList } from "ra-core";
import { useSearchParams } from "react-router";
import type { ReactNode } from "react";
import { BriefcaseBusiness, House, Landmark, UserPlus } from "lucide-react";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import type { Contact, ContactNote } from "../types";
import { DashboardActivityLog } from "./DashboardActivityLog";
import { DashboardStepper } from "./DashboardStepper";
import { LoadsChart } from "./LoadsChart";
import {
  AccountManagerDashboard,
  AccountingDashboard,
  LeadGenDashboard,
} from "./RoleDashboards";
import { TasksList } from "./TasksList";
import { Welcome } from "./Welcome";

const HomeDashboard = () => {
  const {
    data: dataContact,
    total: totalContact,
    isPending: isPendingContact,
  } = useGetList<Contact>("contacts", {
    pagination: { page: 1, perPage: 1 },
  });

  const { total: totalContactNotes, isPending: isPendingContactNotes } =
    useGetList<ContactNote>("contact_notes", {
      pagination: { page: 1, perPage: 1 },
    });

  const { total: totalLoad, isPending: isPendingLoad } = useGetList<Contact>(
    "loads",
    {
      pagination: { page: 1, perPage: 1 },
    },
  );

  const isPending = isPendingContact || isPendingContactNotes || isPendingLoad;

  if (isPending) {
    return null;
  }

  if (!totalContact) {
    return <DashboardStepper step={1} />;
  }

  if (!totalContactNotes) {
    return <DashboardStepper step={2} contactId={dataContact?.[0]?.id} />;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mt-1">
      <div className="md:col-span-9">
        <div className="flex flex-col gap-6">
          {import.meta.env.VITE_IS_DEMO === "true" ? <Welcome /> : null}
          {totalLoad ? <LoadsChart /> : null}
          <DashboardActivityLog />
        </div>
      </div>

      <div className="md:col-span-3">
        <TasksList />
      </div>
    </div>
  );
};

export const Dashboard = () => <DashboardTabs />;

export const DashboardTabs = ({ home }: { home?: ReactNode }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedDashboard = searchParams.get("dashboard");
  const dashboard =
    requestedDashboard &&
    ["home", "lead-gen", "account-manager", "accounting"].includes(
      requestedDashboard,
    )
      ? requestedDashboard
      : "home";

  const setDashboard = (value: string) =>
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      next.set("dashboard", value);
      return next;
    });

  return (
    <Tabs
      value={dashboard}
      onValueChange={setDashboard}
      className="mt-1 gap-5"
    >
      <div className="sm:hidden">
        <Select value={dashboard} onValueChange={setDashboard}>
          <SelectTrigger className="w-full" aria-label="Dashboard">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="home">Home</SelectItem>
            <SelectItem value="lead-gen">Lead Generation</SelectItem>
            <SelectItem value="account-manager">Account Manager</SelectItem>
            <SelectItem value="accounting">Accounting</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <TabsList className="hidden h-11 w-fit min-w-[600px] grid-cols-4 sm:grid">
        <TabsTrigger value="home">
          <House /> Home
        </TabsTrigger>
        <TabsTrigger value="lead-gen" aria-label="Lead Generation">
          <UserPlus /> Lead Gen
        </TabsTrigger>
        <TabsTrigger value="account-manager" aria-label="Account Manager">
          <BriefcaseBusiness /> Account Manager
        </TabsTrigger>
        <TabsTrigger value="accounting">
          <Landmark /> Accounting
        </TabsTrigger>
      </TabsList>
      <TabsContent value="home">{home ?? <HomeDashboard />}</TabsContent>
      <TabsContent value="lead-gen">
        <LeadGenDashboard />
      </TabsContent>
      <TabsContent value="account-manager">
        <AccountManagerDashboard />
      </TabsContent>
      <TabsContent value="accounting">
        <AccountingDashboard />
      </TabsContent>
    </Tabs>
  );
};
